import React, { useEffect, useMemo } from 'react';
import { PageReadOnly as PageBlocks } from '@eaco/forms-ui-web/readonly';
import { usePageFiles } from '@/lib/usePageFiles';
import { renderPortalBlock } from '@/lib/portalPageBlocks';

/**
 * Presentational read-only render of a page document — the editor's "virtual
 * page": an optional cover, an emoji icon, the title, then the blocks. Used by
 * the preview render target and the public share view. The blocks render
 * through the lightweight PageReadOnly (no editor per text block); files
 * resolve up front through the surrounding FormFileTransportProvider.
 */

export type Cover =
  | { kind: 'unsplash'; photo?: { url?: string }; position?: number }
  | { kind: 'url'; url?: string; position?: number }
  | { kind: 'upload'; attachmentId?: string; position?: number }
  | { kind: 'color'; color?: string }
  | { kind: 'gradient'; from?: string; to?: string }
  | null
  | undefined;

export interface PageMeta {
  icon?: string;
  cover?: Cover;
}

export function coverImageUrl(cover: Cover): string | null {
  if (!cover) return null;
  if (cover.kind === 'unsplash') return cover.photo?.url ?? null;
  if (cover.kind === 'url') return cover.url ?? null;
  return null; // upload covers need a signed URL — deferred
}

export function coverBackground(cover: Cover): string | undefined {
  if (!cover) return undefined;
  if (cover.kind === 'color') return cover.color;
  if (cover.kind === 'gradient')
    return `linear-gradient(135deg, ${cover.from}, ${cover.to})`;
  return undefined;
}

/** Vertical focal point (objectPosition Y%) for image covers; default centre. */
export function coverPositionY(cover: Cover): number {
  return (cover && 'position' in cover ? cover.position : undefined) ?? 50;
}

interface Props {
  name: string;
  meta: PageMeta;
  // First page's flat block list (containers extracted by the caller).
  elements: any[];
  /** Called once the page's files have resolved, for render targets. */
  onReady?: () => void;
}

export const PageReadOnly: React.FC<Props> = ({ name, meta, elements, onReady }) => {
  const pages = useMemo(() => [{ elements }], [elements]);
  const { resolveFile, ready } = usePageFiles(pages);
  useEffect(() => {
    if (ready) onReady?.();
  }, [ready, onReady]);
  const cover = meta?.cover;
  const icon = meta?.icon;
  const imgUrl = coverImageUrl(cover);
  const bg = coverBackground(cover);
  const posY = (cover && 'position' in cover ? cover.position : undefined) ?? 50;

  return (
    <div style={{ background: '#eef1f4', minHeight: '100vh', padding: 24 }}>
      <div
        style={{
          maxWidth: 820,
          margin: '0 auto',
          background: '#fff',
          border: '1px solid #e6e9ee',
          borderRadius: 16,
          boxShadow: '0 6px 24px rgba(16,24,40,.06)',
          padding: '28px 32px',
          overflow: 'hidden',
        }}
      >
        {cover && (
          <div
            style={{
              margin: '-28px -32px 0',
              // Thinner than the editor's 210px cover so the thumbnail shows
              // the title + first content blocks, not just the cover.
              height: 120,
              overflow: 'hidden',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              background: bg,
            }}
          >
            {imgUrl && (
              <img
                src={imgUrl}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: `center ${posY}%`,
                  display: 'block',
                }}
              />
            )}
          </div>
        )}
        {icon && (
          <div
            style={{
              fontSize: 60,
              lineHeight: 1,
              marginTop: cover ? 14 : 0,
              marginBottom: 8,
            }}
          >
            {icon}
          </div>
        )}
        <h1
          style={{
            fontSize: 38,
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: '#1f2a3d',
            margin: '0 0 18px',
          }}
        >
          {name || 'Untitled'}
        </h1>
        <PageBlocks content={{ elements }} resolveFile={resolveFile} renderBlock={renderPortalBlock} />
      </div>
    </div>
  );
};

export default PageReadOnly;
